import { FragmentNames, GlycemicRescheduleAliases } from '../../../../codegen'
import BaseController from '../../../BaseController'
import Controller, { OnCancel, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { CheckboxAPI, FieldAPI, FormAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters } from '../../../../utils/parameters';
import { Inject } from '@odinjs/odin';
import { GlycemicService } from './glycemic-service';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import TimelineContext, { PrescriptionType, TimeLineEventMedication } from '../../services/timeline-service';
import { GlycemicControl, GlycemicReschedule as GlycemicRescheduleConsist, RequiredFieldParam78, ScheduleTimeItemPrescriptionRequest } from './glycemic-types';
import { Validator } from '../../../../interfaces/spec-layout';
import { HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../../constants/endpoints';
import { HttpResponse } from '@philips-internal/oswald-ui-core/lib/services';
import { SimpleDialog } from '../../../dialog-helper';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { ApplicationRoute } from '../../../../constants/routes';
import moment from 'moment';
import { DialogType, ModalType, SimpleDialogParams } from '../../../../interfaces/simple-dialog';
import { Value } from '@philips-internal/oswald-ui-core';

const ADEP_1113_MODULE = 'ADEP#1113';
const eliminetedTime = 510990;
const exceededScheduleTime = 1068496;

interface ReasonType {
  reasonDescription: string;
  sequenceNumber: number;
}

interface GlycemicRescheduleResponse {
  eliminatedList: string;
  inconsistentBatchDescription: string;
  message: number;
}

@Controller({ fragment: FragmentNames.GLYCEMIC_RESCHEDULE })
export default class GlycemicRescheduleController extends BaseController {

  @Inject(GlycemicRescheduleAliases.CHECKBOX_RESCHEDULE)
  private checkboxApi!: CheckboxAPI;

  @Inject(GlycemicRescheduleAliases.FIELD_JUSTIFICATION)
  private justificationFieldApi!: FieldAPI;

  @Inject(GlycemicRescheduleAliases.FIELD_REASON)
  private reasonFieldApi!: FieldAPI;

  @Inject(GlycemicRescheduleAliases.FORM_SCHEDULE)
  private formApi!: FormAPI;

  @OnMount(GlycemicRescheduleAliases.FORM_SCHEDULE)
  onMountForm(): void {
    this.mountInformationBar();
    this.mountContextAndFields();
    this.checkFunctionParams();
  }

  @OnSubmit(GlycemicRescheduleAliases.FORM_SCHEDULE)
  onSubmitForm(): void {
    this.consistReschedule();
  }

  @OnCancel(GlycemicRescheduleAliases.FORM_SCHEDULE)
  onCancelForm(): void {
    this.historyService.back();
    applicationContext.popNavigationItem();
  }

  private mountInformationBar(): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    this.context.set({
      encounterId,
      patientId,
    });
  }

  private mountContextAndFields(): void {
    const { status, start } = TimelineContext.get();

    this.context.set({
      ...GlycemicService.instance,
      establishmentCode: foundationContext.getEstablishment()?.id,
      status: status,
    });

    this.formApi.setData({
      desiredtime: start,
    });
  }

  private checkFunctionParams(): void {
    this.checkParamsCheckbox();
    this.checkParamsReason();
  }

  private checkParamsCheckbox(): void {
    const param100 = FoundationUtils.findModuleParam(ADEP_1113_MODULE, ADEP_E1113Parameters.WHILE_RESCHEDULE_SHOW_RESCHEDULE_FOLLOWING_HOURS_CHECKBOX_BY_DEFAULT);
    this.checkboxApi.setValue(param100 === 'S');
  }

  private checkParamsReason(): void {
    const param78 = FoundationUtils.findModuleParam(ADEP_1113_MODULE, ADEP_E1113Parameters.FIELD_TO_BE_REQUIRED_FOR_RESCHEDULE) as RequiredFieldParam78;
    this.addRequiredField(param78);
  }

  private addRequiredField(requiredField: RequiredFieldParam78): void {
    const validator: Validator = { name: 'required', options: {} };
    switch (requiredField) {
      case RequiredFieldParam78.REASON:
        this.reasonFieldApi.addValidator(validator);
        break;
      case RequiredFieldParam78.JUSTIFICATION:
        this.justificationFieldApi.addValidator(validator);
        break;
      case RequiredFieldParam78.BOTH:
        this.justificationFieldApi.addValidator(validator);
        this.reasonFieldApi.addValidator(validator);
        break;
    }
  }

  private consistReschedule(): void {
    const body = this.buildRecheduleBody();

    const params = {
      prescriptionNumber: TimelineContext.get().prescriptionNumber
    }

    HttpService.post<GlycemicRescheduleResponse>(Endpoint.CONSIST_SCHEDULE, params, { body })
      .then((response: any) => this.normalizeResponse(response))
      .catch((response: { body: { errors: string[]; }; }) => response.body?.errors.forEach((message: string) => this.showToastError(message)));
  }

  private rescheduleTime(): void {
    const { cpoeNumber, observationDescription, scheduleSequenceNumber, pdoPrn } = this.context.getBulkData() as GlycemicControl;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    const body: ScheduleTimeItemPrescriptionRequest | Record<string, Value> = {
      ...this.buildRecheduleBody(),
      cpoeNumber,
      functionCode: 9129,
      observationDescription,
      scheduleItemCicleStatus: 'N',
      scheduleSequenceNumber,
      statusAcm: pdoPrn ? 'S' : 'N',
      statusApplication: TimelineContext.get().administrationRouteType
    }

    HttpService.post<GlycemicRescheduleResponse>(Endpoint.SCHEDULE_TIME_ITEM_PRESCRIPTION, undefined, { body })
      .then(response => {
        if (!response.body) {
          return;
        }

        const { message, eliminatedList, inconsistentBatchDescription } = response.body
        applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
          encounterId,
          patientId
        });

        applicationContext.clearNavigationStack();

        if (message === eliminetedTime) {
          if (eliminatedList != null && inconsistentBatchDescription != null) {
            this.showToastTimeItemPrescription(`${inconsistentBatchDescription} ${eliminatedList}`);
          } else if (eliminatedList != null) {
            this.showToastTimeItemPrescription(eliminatedList)
          } else if (inconsistentBatchDescription != null) {
            this.showToastTimeItemPrescription(inconsistentBatchDescription)
          }
          return;
        }

        if(message === exceededScheduleTime) {
          this.showToast('text.not-possible-reschedule-item', ToastLevel.WARNING);
          return;
        }

        this.showToastSuccess();
      }).catch(response => {
        response.body?.errors.forEach((message: string) => this.showToastError(message))
      })
  }

  private buildRecheduleBody(): GlycemicRescheduleConsist | Record<string, Value> {
    const consistRescheduleItemTime = FoundationUtils.findModuleParam(ADEP_1113_MODULE, ADEP_E1113Parameters.SHOW_CONSISTENCY_REGARDING_THE_RESCHEDULIN_OF_AN_ITEM) as string;
    const schedulePrevious = this.checkboxApi.getValue() ? 'S' : 'N'
    const formData = this.formApi.getRecord();
    const reasonField = formData.reason as ReasonType;
    const { prescriptionNumber, prescriptionsNumber } = TimelineContext.get();
    return {
      consistRescheduleItemTime,
      desiredTime: moment(formData.desiredtime as string).utcOffset(0, true).format(),
      encounterNumber: this.context.get('encounterId'),
      establishmentCode: foundationContext.getEstablishment()?.id as string,
      itemType: TimelineContext.get().itemType,
      justificationDescription: this.justificationFieldApi.getValue() as string,
      laboratoryStatus: 'N',
      prescriptionId: prescriptionNumber,
      prescriptionsIds: prescriptionsNumber ? prescriptionsNumber : prescriptionNumber,
      reasonNumber: reasonField?.sequenceNumber || 0,
      scheduleTime: schedulePrevious,
      userName: foundationContext.getUser()?.username || '',
      ...this.getBodyByType(TimelineContext.get().itemType)
    };
  }

  private getBodyByType(itemType: string): Record<string, Value> {
    const timelineEvent = TimelineContext.get() as TimeLineEventMedication;
    const glycemicControl = this.context.getBulkData() as unknown as GlycemicControl;
    const isGlycemicControl = itemType === PrescriptionType.GLYCEMIC_CONTROL;
    this.context.set({cpoeNumber: timelineEvent?.cpoeSequence});

    return isGlycemicControl
      ?
      {
        currentTime: glycemicControl.scheduleDate,
        internalProcedureNumber: glycemicControl.internalProcedureSequence,
        intervalCode: glycemicControl.intervalCode,
        itemCode: glycemicControl.procedureCode,
        itemQuantity: glycemicControl.doseQuantity,
        itemSequence: glycemicControl.sequenceProcedure
      }
      :
      {
        currentTime: timelineEvent.start,
        internalProcedureNumber: timelineEvent.internalProcedureSequence,
        intervalCode: timelineEvent.intervalCode,
        itemCode: timelineEvent.itemCode,
        itemQuantity: timelineEvent.itemQuantity,
        itemSequence: timelineEvent.itemSequence,
      }
  }

  private normalizeResponse(response: HttpResponse<GlycemicRescheduleResponse>): void {
    const body = response.body;

    switch(body?.message) {
      case 616381:
        this.confirmCancel(body.eliminatedList);
        break;
      case 739552:
        this.showToastError();
        break;
      case 510988:
        this.showToastErrorValidationData();
        applicationContext.popNavigationItem();
        break;
      case 0:
        this.rescheduleTime();
    }
  }

  private showToastError(message?: string): void {
    this.showToast(message || 'tws.schedule-medication-infusion-error', ToastLevel.ERROR);
  }

  private showToastErrorValidationData(): void {
    this.showToast('tws.schedule-medication-infusions-validation-date', ToastLevel.ERROR);
  }

  private showToastTimeItemPrescription(value: string): void {
    const message = MessageService.translate('tws.schedule-medication-infusions-time').replace('#@eliminados#@', value);
    this.showToast(message, ToastLevel.WARNING);
  }

  private showToastSuccess(): void {
    this.showToast('tws.schedule-medication-infusions-success', ToastLevel.SUCCESS);
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'schedule-error-validation',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private confirmCancel(value?: string): void {
    const message = MessageService.translate('tws.schedule-medicadication-infusion-reschedule').replace('#@horarios#@', `${value}`);

    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: message,
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickOk: async () => { this.rescheduleTime() },
      titleDialog: '',
      titleOnCancel: 'label.status-no',
      titleOnOk: 'label.status-yes',
    }

    SimpleDialog.openDialog(dialogParams);
  }
}
